/**
 * Auth API client for enhanced authentication
 */

import { AUTH_API_URL } from './config';
import { createLogger } from '@yada/logger';

const logger = createLogger('fetchWithAuth');

export interface MagicLinkRequestResponse {
  message: string;
  _dev_token?: string;
}

export interface MagicLinkTotpRequiredResponse {
  status: 'totp_required';
  account_id: string;
}

export interface LoginResponse {
  /** Server receipt: recovery proof and all configured factors completed. */
  passkey_recovery?: boolean;
  access_token: string;
  token_type: string;
  expires_in: number;
  account: {
    id: string;
    email: string;
    created_at?: string;
    recovery_mode?: string;
    security_prefs?: Record<string, unknown>;
    enabled_apps?: string[];
    pinned_apps?: string[];
    onboarding_complete?: boolean;
    server_synced?: boolean;
    email_verified?: boolean;
  };
  profile: {
    id: string;
    account_id?: string;
    name: string;
    profile_type: string;
    verification_level?: number;
    visibility?: string;
    created_at?: string;
    updated_at?: string;
  };
  workspace_ids: string[];
  device_id: string;
}

export interface RefreshResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

/**
 * Login with email and profile
 */
export async function login(
  email: string,
  profileId?: string,
  deviceId?: string
): Promise<LoginResponse> {
  const response = await fetch(`${AUTH_API_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for refresh token
    body: JSON.stringify({
      email,
      profile_id: profileId,
      device_id: deviceId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Login failed');
  }

  return response.json();
}

/**
 * The auth service could not decide (5xx). The session may still be valid, so
 * callers keep it and retry later instead of signing out.
 */
export class AuthServiceUnavailableError extends Error {
  constructor() { super('Authentication service unavailable'); }
}

function isTransientRefreshFailure(err: any): boolean {
  return err instanceof AuthServiceUnavailableError || !navigator.onLine ||
    (err instanceof TypeError && err.message?.includes('fetch')) ||
    err?.message?.includes('Failed to fetch') ||
    err?.message?.includes('NetworkError');
}

/**
 * Refresh access token using refresh token cookie
 */
export async function refreshAccessToken(): Promise<RefreshResponse> {
  const before = getAccessToken();
  const accountScope = localStorage.getItem('yada:account:account');
  const run = async (): Promise<RefreshResponse> => {
    if (localStorage.getItem('yada:account:account') !== accountScope) {
      throw new Error('Account changed during token refresh');
    }
    // Another tab may have rotated the shared cookie while this tab waited.
    const current = getAccessToken();
    if (current && current !== before) {
      return { access_token: current, token_type: 'Bearer',
        expires_in: Math.max(0, Math.floor((getJwtExpiryMs(current)! - Date.now()) / 1000)) };
    }
    const response = await fetch(`${AUTH_API_URL}/api/auth/refresh`, {
      method: 'POST', credentials: 'include',
    });
    if (response.status >= 500) throw new AuthServiceUnavailableError();
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Token refresh failed');
    }
    const result: RefreshResponse = await response.json();
    if (localStorage.getItem('yada:account:account') !== accountScope) {
      throw new Error('Account changed during token refresh');
    }
    setAccessToken(result.access_token, result.expires_in);
    return result;
  };
  // Cookie rotation must serialize across tabs, not just concurrent calls in this module.
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('yada:auth:refresh', run);
  }
  return run();
}

/**
 * Logout and revoke refresh token
 */
export async function logout(): Promise<void> {
  await fetch(`${AUTH_API_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

/**
 * Get current access token from localStorage (persists across module boundaries)
 */
const TOKEN_KEY = 'yada:access_token';
const TOKEN_EXPIRY_KEY = 'yada:access_token_expires_at';

export function setAccessToken(token: string, expiresIn: number) {
  const expiresAt = Math.min(Date.now() + expiresIn * 1000, getJwtExpiryMs(token) ?? Infinity);
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
  } catch (err) {
    logger.error('Failed to store access token:', err);
  }
}

export function getAccessToken(): string | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAtStr = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiresAtStr) {
      return null;
    }

    // Older auth responses advertised a one-year TTL for a 15-minute JWT.
    // Honor the signed token's expiry even for sessions saved before this fix.
    const expiresAt = Math.min(parseInt(expiresAtStr, 10), getJwtExpiryMs(token) ?? Infinity);

    // Check if token is expired
    if (!Number.isFinite(expiresAt) || Date.now() + 30_000 >= expiresAt) {
      clearAccessToken();
      return null;
    }

    return token;
  } catch (err) {
    logger.error('Failed to retrieve access token:', err);
    return null;
  }
}

function getJwtExpiryMs(token: string): number | null {
  try {
    const encoded = token.split('.')[1];
    if (!encoded) return null;
    const binary = atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(new TextDecoder().decode(Uint8Array.from(binary, c => c.charCodeAt(0))));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function clearAccessToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (err) {
    logger.error('Failed to clear access token:', err);
  }
}

/**
 * Get a valid access token, automatically refreshing if missing or expired.
 */
export async function getValidAccessToken(): Promise<string | null> {
  let token = getAccessToken();
  if (!token) {
    const accountStr = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:account') : null;
    if (accountStr) {
      try {
        const account = JSON.parse(accountStr);
        if (account.isAccountless || account.isAccountless === 'true') {
          return null;
        }
      } catch {
        // ignore parse error
      }
    }
    try {
      const refreshed = await refreshOnce();
      setAccessToken(refreshed.access_token, refreshed.expires_in);
      token = refreshed.access_token;
    } catch {
      return null;
    }
  }
  return token;
}

// Deduplicates concurrent refresh calls — token rotation means only one can succeed.
let _refreshPromise: Promise<RefreshResponse> | null = null;
function refreshOnce(): Promise<RefreshResponse> {
  if (!_refreshPromise) {
    _refreshPromise = refreshAccessToken().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}

/** Force a fresh JWT after a storage node rejects a cached one. Never clears local keys. */
export async function refreshSessionToken(): Promise<string> {
  const refreshed = await refreshOnce();
  setAccessToken(refreshed.access_token, refreshed.expires_in);
  return refreshed.access_token;
}

/**
 * Schedule account deletion (30-day grace period).
 * Returns the scheduled deletion date.
 */
export async function scheduleAccountDeletion(): Promise<{ deletion_scheduled_at: string }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/account`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as any).message || (body as any).error || 'Failed to schedule deletion');
  }
  return response.json();
}

/**
 * Cancel a previously scheduled account deletion.
 */
export async function cancelAccountDeletion(): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/account/cancel-deletion`, { method: 'POST' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as any).message || (body as any).error || 'Failed to cancel deletion');
  }
}

/**
 * Request a magic link for the given email.
 */
export async function requestMagicLink(email: string, createAccount = false): Promise<MagicLinkRequestResponse> {
  const response = await fetch(`${AUTH_API_URL}/api/auth/magic-link/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, create_account: createAccount }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Failed to send magic link');
  }


  return response.json();
}

/**
 * Verify a magic link token (optionally with TOTP code for 2FA accounts).
 * Returns LoginResponse on success or MagicLinkTotpRequiredResponse if TOTP is needed.
 */
export async function verifyMagicLink(token: string, totpCode?: string, deviceId?: string | null): Promise<LoginResponse | MagicLinkTotpRequiredResponse> {
  const payload: any = { token };
  if (totpCode) payload.totp_code = totpCode;
  if (deviceId) payload.device_id = deviceId;
  
  // Email/Google identity does not prove ownership of an existing device ID.
  // Decrypt a single-use challenge using the original browser key before reuse.
  if (deviceId && !token.startsWith('passkey.')) {
    const challengeResponse = await fetch(`${AUTH_API_URL}/api/auth/magic-link/device-proof`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, device_id: deviceId }),
    });
    const challenge = await challengeResponse.json();
    if (!challengeResponse.ok) throw new Error(challenge.error || 'Device proof unavailable');
    if (challenge.ticket) {
      const { getDeviceKey, unwrapSeed } = await import('@yada/crypto');
      const device = await getDeviceKey(deviceId);
      if (!device || device.deviceId !== deviceId || challenge.device_id !== deviceId) throw new Error('Existing device key mismatch. Use linking or recovery.');
      const encoded = challenge.proof.replace(/-/g, '+').replace(/_/g, '/');
      const secret = await unwrapSeed(Uint8Array.from(atob(encoded), character => character.charCodeAt(0)).buffer, device.privateKey);
      payload.device_ticket = challenge.ticket;
      payload.device_proof = btoa(String.fromCharCode(...new Uint8Array(secret))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
  }

  const response = await fetch(`${AUTH_API_URL}/api/auth/magic-link/verify-json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Verification failed');
  }

  return response.json();
}

/**
 * Start TOTP enrollment — returns secret and URI for authenticator app.
 */
export async function enrollTotp(): Promise<{ secret: string; uri: string; issuer: string; digits: number; period: number }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/totp/enroll`, { method: 'POST' });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Failed to start TOTP enrollment');
  }

  return response.json();
}

/**
 * Confirm TOTP enrollment by verifying the first code.
 */
export async function verifyTotpEnrollment(code: string): Promise<{ verified: boolean }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/totp/enroll/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Failed to verify TOTP code');
  }

  return response.json();
}

/**
 * Get TOTP enrollment status for the current account.
 */
export async function getTotpStatus(): Promise<{ enrolled: boolean; pending_verification: boolean; last_used_at: string | null }> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/totp/status`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Failed to get TOTP status');
  }

  return response.json();
}

/**
 * Unenroll TOTP for the current account.
 */
export async function unenrollTotp(code: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/auth/totp/enroll`, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Failed to disable TOTP');
  }
}

/**
 * Fetch with automatic token refresh
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token = getAccessToken();
  logger.log('Token from localStorage:', token ? 'found' : 'not found');

  // If no token or expired, try to refresh
  if (!token) {
    // Check if account is accountless — if so, we don't have a token and shouldn't refresh
    const accountStr = localStorage.getItem('yada:account:account');
    if (accountStr) {
      try {
        const account = JSON.parse(accountStr);
        if (account.isAccountless || account.isAccountless === 'true') {
          logger.log('Account is accountless, skipping refresh and Authorization header');
          return fetch(url, options);
        }
      } catch (_e) {
        // ignore parse error
      }
    }

    logger.log('No token, attempting refresh...');
    try {
      const refreshed = await refreshOnce();
      setAccessToken(refreshed.access_token, refreshed.expires_in);
      token = refreshed.access_token;
      logger.log('Token refreshed successfully');
    } catch (err: any) {
      logger.error('Refresh failed:', err);
      // If we are offline or the auth service is down, do not expire the
      // session — let the app run with local data.
      const isNetworkError = isTransientRefreshFailure(err);

      if (!isNetworkError) {
        // Notify the UI so it shows a message instead of silently logging out.
        window.dispatchEvent(new CustomEvent('yada:session-expired'));
      }
      throw new Error(isNetworkError ? 'Network unavailable' : 'Authentication required');
    }
  }

  // Add Authorization header
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  logger.log('Making request to:', url, 'with Authorization header');

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Ensure cookies are sent
  });

  // If 401, try to refresh once
  if (response.status === 401) {
    try {
      const refreshed = await refreshOnce();
      setAccessToken(refreshed.access_token, refreshed.expires_in);

      // Retry request with new token
      headers.set('Authorization', `Bearer ${refreshed.access_token}`);
      return fetch(url, { ...options, headers, credentials: 'include' });
    } catch (err: any) {
      const isNetworkError = isTransientRefreshFailure(err);

      if (!isNetworkError) {
        clearAccessToken();
        // Notify the UI that the session could not be restored.
        window.dispatchEvent(new CustomEvent('yada:session-expired'));
      }
      throw new Error(isNetworkError ? 'Network unavailable' : 'Authentication failed');
    }
  }

  return response;
}
